import {curry, isEmpty, tap} from 'ramda';

import {logEvent as gaLog, logError} from './common';

let counters = {};
let events = {};

// This is exported to simplify testing validation, it uses shallow copies intentionally
export const _status = () => ({
    counters: {...counters},
    events: {...events},
});

const key = ({category, action, label}) => JSON.stringify({category, action, label}).toLowerCase();

export const GA_ALERT_CATEGORY         = 'Alert';
export const GA_BIO_CATEGORY           = 'Bio';
export const GA_HELP_CATEGORY          = 'Help';
export const GA_IMAGE_PACKAGE_CATEGORY = 'Package Image Editing';
export const GA_MEDIA_CATEGORY         = 'Media';
export const GA_MODULE_CATEGORY        = 'Modules';
export const GA_STORY_CATEGORY         = 'Story';
export const GA_STORY_PACKAGE_CATEGORY = 'Story Package Editing';
export const GA_PROPERTY_CATEGORY      = 'Property Management';
export const GA_TEASER_LIST_CATEGORY   = 'Teaser Placement';
export const GA_VIDEO_PACKAGE_CATEGORY = 'Package Video Editing';
export const GA_SHOWS_CATEGORY         = 'Show Package Editing';

const canBeLogged = ({label = ''}) => !isEmpty(label);

const incrementCounter = name => {
    if (!counters[name]) {
        counters[name] = 1;
    } else {
        counters[name] += 1;
    }

    return counters[name];
};

const addEvent = gaParams => {
    const eventKey = key(gaParams);
    const event = events[eventKey] ? events[eventKey] : (events[eventKey] = gaParams);

    event.value = incrementCounter(eventKey);

    return Promise.resolve(event);
};

export const logEvent = (gaParams, forceLog = false) => {
    if (canBeLogged(gaParams)) {
        if (forceLog) {
            return gaLog(gaParams);
        } else {
            return addEvent(gaParams);
        }
    }
    return Promise.resolve();
};

const deleteEvent = gaParams => delete events[key(gaParams)];

export const commitLog = () => {
    const results = [];

    for (const eventName in events) {
        if (events.hasOwnProperty(eventName)) {
            const event = events[eventName];

            results.push(
                gaLog(event)
                    .then(tap(() => deleteEvent(event)))
                    .catch(err => {
                        deleteEvent(event);
                        logError(err);
                    }),
            );
        }
    }

    return Promise.all(results);
};

export const resetCounters = () => {
    for (const name in counters) {
        if (counters.hasOwnProperty(name)) {
            counters[name] = 0;
        }
    }
};

export const reset = () => {
    events = {};
    counters = {};
};

export const customEvent = curry((category, forceLog, action, opts) =>
    logEvent(
        {
            category,
            action,
            ...opts,
        },
        forceLog,
    ),
);

export const alertEvent            = customEvent(GA_ALERT_CATEGORY);
export const bioEvent              = customEvent(GA_BIO_CATEGORY);
export const helpEvent             = customEvent(GA_HELP_CATEGORY);
export const imagePackageEvent     = customEvent(GA_IMAGE_PACKAGE_CATEGORY);
export const mediaEvent            = customEvent(GA_MEDIA_CATEGORY);
export const moduleEvent           = customEvent(GA_MODULE_CATEGORY);
export const storyEvent            = customEvent(GA_STORY_CATEGORY);
export const storyPackageEditEvent = customEvent(GA_STORY_PACKAGE_CATEGORY);
export const propertyEvent         = customEvent(GA_PROPERTY_CATEGORY);
export const teaserListEvent       = customEvent(GA_TEASER_LIST_CATEGORY);
export const videoPackageEvent     = customEvent(GA_VIDEO_PACKAGE_CATEGORY);
export const showPackageEvent      = customEvent(GA_SHOWS_CATEGORY);

export default {
    logError,
    logEvent,
    commitLog,
    resetCounters,
    reset,
};
